"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { format } from "date-fns";
import { useMemo, useRef, useState } from "react";
import type { ConfigurationItemAddon } from "@/features/addons/types/configuration-item-addon.type";

const DATE_FORMAT = "yyyy-MM-dd";
export const toDateKey = (date: Date) => format(date, DATE_FORMAT);

type UseDietAddonModalParams = {
  // The addon being configured, or null when the summary dialog shows its
  // overview and no add/edit view is mounted yet.
  addon: AddonDietAddonsRead | null;
  // Diet delivery days chosen in the calendar above — the only days an addon
  // can be attached to.
  dietDates: Date[];
  // Previously committed entries for this addon (re-open / edit).
  initialEntries: ConfigurationItemAddon[];
};

export const useDietAddonModal = ({
  addon,
  dietDates,
  initialEntries,
}: UseDietAddonModalParams) => {
  // `limitForDiet` caps the total quantity of this addon across all diet days.
  // A value of 0 or null means there is no per-diet limit (unlimited), so treat
  // it as infinity to keep every quantity/validity comparison working.
  const rawLimit = addon?.limitForDiet ?? 0;
  const limit = rawLimit > 0 ? rawLimit : Number.POSITIVE_INFINITY;

  // Unique, chronologically sorted diet days.
  const allowedDates = useMemo(() => {
    const unique = new Map<string, Date>();
    for (const date of dietDates) {
      unique.set(toDateKey(date), date);
    }
    return [...unique.values()].sort((a, b) => a.getTime() - b.getTime());
  }, [dietDates]);

  const allowedKeys = useMemo(
    () => new Set(allowedDates.map(toDateKey)),
    [allowedDates],
  );

  // Committed entries that still fall on a diet delivery day — anything else is
  // stale and cannot be restored into the picker.
  const restoredEntries = initialEntries.filter((entry) =>
    allowedKeys.has(entry.date),
  );

  // An addon with committed days opens in edit mode: the label switches to
  // "confirm" and clearing every day is allowed, because that is how the user
  // removes the addon (MPL-3844).
  const isEditing = restoredEntries.length > 0;

  // Seed the per-day selection: restore committed entries when present,
  // otherwise pre-select the first diet day with a single unit.
  const seedSelection = (): Record<string, number> => {
    if (!addon) {
      return {};
    }
    if (isEditing) {
      return Object.fromEntries(
        restoredEntries.map((entry) => [entry.date, entry.quantity]),
      );
    }
    const firstDay = allowedDates.at(0);
    return firstDay ? { [toDateKey(firstDay)]: 1 } : {};
  };

  // Quantity per selected day, keyed by yyyy-MM-dd.
  const [selection, setSelection] =
    useState<Record<string, number>>(seedSelection);

  // Re-seed the selection whenever the configured addon changes. The summary
  // dialog reuses a single hook instance and swaps the active addon in place,
  // so a stale selection from the previous addon must be discarded.
  const previousAddonId = useRef(addon?.id);
  if (previousAddonId.current !== addon?.id) {
    previousAddonId.current = addon?.id;
    setSelection(seedSelection());
  }

  const totalQuantity = useMemo(
    () => Object.values(selection).reduce((sum, quantity) => sum + quantity, 0),
    [selection],
  );

  const canAddMore = totalQuantity < limit;

  const selectedDates = useMemo(
    () =>
      allowedDates.filter((date) => selection[toDateKey(date)] !== undefined),
    [allowedDates, selection],
  );

  const isAllSelected =
    allowedDates.length > 0 && selectedDates.length === allowedDates.length;

  // Price: promo price when present, plus one-time deposits (kaucje) per unit.
  const unitPrice = addon ? (addon.promoPrice ?? addon.price) : 0;
  const depositPerUnit =
    addon?.deposits.reduce((sum, deposit) => sum + deposit.cost, 0) ?? 0;
  const perUnitPrice = unitPrice + depositPerUnit;
  const totalPrice = totalQuantity * perUnitPrice;

  const isWithinLimit = totalQuantity <= limit;
  // Adding requires at least one day; editing may end with none, which commits
  // an empty selection and removes the addon.
  const canCommit = isWithinLimit && (isEditing || totalQuantity > 0);
  // Committing with no day left is a removal, not an update.
  const isRemoval = isEditing && totalQuantity === 0;

  // Reconcile the calendar's multiple-select against the current selection,
  // preserving per-day quantities and clamping new days to limitForDiet.
  const syncSelectedDates = (dates: Date[] | undefined) => {
    const nextKeys = new Set((dates ?? []).map(toDateKey));
    setSelection((current) => {
      const next: Record<string, number> = {};
      for (const [key, quantity] of Object.entries(current)) {
        if (nextKeys.has(key)) {
          next[key] = quantity;
        }
      }
      let running = Object.values(next).reduce((sum, qty) => sum + qty, 0);
      for (const date of dates ?? []) {
        const key = toDateKey(date);
        if (!(key in next) && allowedKeys.has(key) && running < limit) {
          next[key] = 1;
          running += 1;
        }
      }
      return next;
    });
  };

  const incrementDay = (date: Date) => {
    if (!canAddMore) {
      return;
    }
    const key = toDateKey(date);
    setSelection((current) =>
      current[key] === undefined
        ? current
        : { ...current, [key]: current[key] + 1 },
    );
  };

  const decrementDay = (date: Date) => {
    const key = toDateKey(date);
    setSelection((current) => {
      if (current[key] === undefined) {
        return current;
      }
      const quantity = current[key] - 1;
      if (quantity <= 0) {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: quantity };
    });
  };

  const removeDay = (date: Date) => {
    const key = toDateKey(date);
    setSelection((current) => {
      const { [key]: _removed, ...rest } = current;
      return rest;
    });
  };

  const toggleAll = () => {
    setSelection(() => {
      if (isAllSelected) {
        return {};
      }
      // Select as many diet days as the limit allows, one unit each.
      return Object.fromEntries(
        allowedDates.slice(0, limit).map((date) => [toDateKey(date), 1]),
      );
    });
  };

  const buildEntries = (): { date: string; quantity: number }[] =>
    Object.entries(selection).map(([date, quantity]) => ({ date, quantity }));

  return {
    allowedDates,
    allowedKeys,
    selectedDates,
    selection,
    totalQuantity,
    canAddMore,
    isAllSelected,
    unitPrice,
    perUnitPrice,
    totalPrice,
    isEditing,
    canCommit,
    isRemoval,
    syncSelectedDates,
    incrementDay,
    decrementDay,
    removeDay,
    toggleAll,
    buildEntries,
  };
};
